import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, RefreshCw, LogOut, Plus, Pencil, Trash2, Sparkles, Send } from "lucide-react";
import { API, ARTICLE_CATEGORIES, CATEGORY_TINTS } from "@/lib/site";
import { Wordmark } from "@/components/Navbar";

const STATUS_STYLES = {
  new: "border-emerald-600/40 bg-[#DDF7EC] text-emerald-700",
  contacted: "border-amber-500/40 bg-[#FFF7C7] text-amber-700",
  closed: "border-[#10202E]/15 bg-[#10202E]/[0.04] text-[#6E8698]",
};

const emptyDraft = { title: "", category: "Financial Planning", time: "4 min read", excerpt: "", body: "", cover: "" };

const inputCls =
  "w-full rounded-xl border border-[#10202E]/20 bg-transparent px-4 py-3 text-sm text-[#10202E] placeholder:text-[#8AA0B0] focus:border-emerald-600 focus:outline-none";

export default function Admin() {
  const [key, setKey] = useState(sessionStorage.getItem("hrk_admin_key") || "");
  const [authed, setAuthed] = useState(false);
  const [enquiries, setEnquiries] = useState([]);
  const [articles, setArticles] = useState([]);
  const [leads, setLeads] = useState([]);
  const [tab, setTab] = useState("enquiries");
  const [editor, setEditor] = useState(null);
  const [draft, setDraft] = useState(emptyDraft);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [followUpId, setFollowUpId] = useState("");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const headers = (k) => ({ headers: { "X-Admin-Key": k } });

  const load = async (k) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/enquiries`, headers(k));
      setEnquiries(res.data.enquiries);
      setAuthed(true);
      sessionStorage.setItem("hrk_admin_key", k);
      const arts = await axios.get(`${API}/articles`);
      setArticles(arts.data.articles);
      const lds = await axios.get(`${API}/leads`, headers(k));
      setLeads(lds.data.leads);
    } catch {
      setError("Invalid access key.");
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  const refresh = async () => {
    if (tab === "enquiries") {
      const res = await axios.get(`${API}/enquiries`, headers(key));
      setEnquiries(res.data.enquiries);
    } else if (tab === "journal") {
      const res = await axios.get(`${API}/articles`);
      setArticles(res.data.articles);
    } else {
      const res = await axios.get(`${API}/leads`, headers(key));
      setLeads(res.data.leads);
    }
  };

  useEffect(() => {
    const saved = sessionStorage.getItem("hrk_admin_key");
    if (saved) load(saved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/enquiries/${id}`, { status }, headers(key));
      setEnquiries((list) => list.map((e) => (e.id === id ? { ...e, status } : e)));
    } catch {
      setError("Could not update status.");
    }
  };

  const openNew = () => {
    setDraft(emptyDraft);
    setEditor({ mode: "new" });
    setNotice("");
  };

  const openEdit = (a) => {
    setDraft({ title: a.title, category: a.category, time: a.time, excerpt: a.excerpt, body: a.body.join("\n\n"), cover: a.cover || "" });
    setEditor({ mode: "edit", id: a.id });
    setNotice("");
  };

  const saveArticle = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = { ...draft, cover: draft.cover || null };
      if (editor.mode === "new") {
        await axios.post(`${API}/articles`, payload, headers(key));
      } else {
        await axios.put(`${API}/articles/${editor.id}`, payload, headers(key));
      }
      const res = await axios.get(`${API}/articles`);
      setArticles(res.data.articles);
      setEditor(null);
      setNotice(editor.mode === "new" ? "Article published." : "Article updated.");
    } catch (err) {
      setError(err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail || "Could not save the article.");
    } finally {
      setSaving(false);
    }
  };

  const deleteArticle = async (id) => {
    if (!window.confirm("Delete this article? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/articles/${id}`, headers(key));
      setArticles((list) => list.filter((a) => a.id !== id));
      setNotice("Article deleted.");
    } catch {
      setError("Could not delete the article.");
    }
  };

  const generateCover = async () => {
    setGenerating(true);
    setError("");
    setNotice("");
    try {
      const res = await axios.post(
        `${API}/articles/generate-cover`,
        { title: draft.title.trim(), excerpt: draft.excerpt.trim() },
        headers(key)
      );
      setDraft((d) => ({ ...d, cover: res.data.cover }));
      setNotice("Cover art generated — it will be saved with the article.");
    } catch (err) {
      setError(err.response?.data?.detail || "Cover generation failed. Please try again.");
    } finally {
      setGenerating(false);
    }
  };

  const sendFollowUp = async (id) => {
    setFollowUpId(id);
    setError("");
    setNotice("");
    try {
      const res = await axios.post(`${API}/leads/${id}/follow-up`, {}, headers(key));
      setLeads((list) => list.map((l) => (l.id === id ? { ...l, followed_up_at: res.data.followed_up_at } : l)));
      setNotice("Follow-up email sent.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(
        typeof detail === "string" && !detail.includes("<")
          ? detail
          : "Could not send the follow-up email — the address may be unreachable."
      );
    } finally {
      setFollowUpId("");
    }
  };

  const logout = () => {
    sessionStorage.removeItem("hrk_admin_key");
    setAuthed(false);
    setKey("");
    setEnquiries([]);
    setArticles([]);
  };

  return (
    <div className="min-h-screen bg-[#F8F7F3] px-6 py-10 text-[#10202E]">
      <div className="mx-auto max-w-4xl">
        <div className="flex items-center justify-between">
          <Wordmark onClick={() => { window.location.href = "/"; }} />
          {authed && (
            <div className="flex gap-3">
              <button
                type="button"
                data-testid="admin-refresh-button"
                onClick={refresh}
                className="inline-flex items-center gap-2 rounded-full border border-[#10202E]/15 px-4 py-2 text-xs text-[#33506A] transition-colors hover:border-emerald-600/50"
              >
                <RefreshCw className="h-3.5 w-3.5" /> Refresh
              </button>
              <button
                type="button"
                data-testid="admin-logout-button"
                onClick={logout}
                className="inline-flex items-center gap-2 rounded-full border border-[#10202E]/15 px-4 py-2 text-xs text-[#33506A] transition-colors hover:border-red-400/60 hover:text-red-500"
              >
                <LogOut className="h-3.5 w-3.5" /> Log out
              </button>
            </div>
          )}
        </div>

        {!authed ? (
          <div className="mx-auto mt-24 max-w-sm rounded-3xl border border-[#10202E]/[0.08] bg-white p-8 shadow-[0_20px_60px_rgba(16,32,46,0.08)]">
            <h1 className="font-display text-3xl text-[#10202E]">Admin</h1>
            <p className="mt-2 text-sm font-light text-[#4E6A82]">
              Enter the admin access key to manage enquiries and journal articles.
            </p>
            <form
              className="mt-8 space-y-5"
              onSubmit={(e) => {
                e.preventDefault();
                load(key);
              }}
            >
              <input
                data-testid="admin-key-input"
                type="password"
                value={key}
                onChange={(e) => setKey(e.target.value)}
                placeholder="Access key"
                className={inputCls}
              />
              {error && (
                <p data-testid="admin-error" className="text-xs text-red-500">
                  {error}
                </p>
              )}
              <button
                type="submit"
                data-testid="admin-login-button"
                disabled={loading}
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-emerald-400 px-6 py-3 text-sm font-semibold text-[#04120c] transition-colors hover:bg-emerald-300 disabled:opacity-60"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Log in"}
              </button>
            </form>
          </div>
        ) : (
          <div className="mt-10">
            <div className="inline-flex gap-1.5 rounded-full border border-[#10202E]/10 bg-white/70 p-1.5">
              {[
                { id: "enquiries", label: "Enquiries" },
                { id: "journal", label: "Journal" },
                { id: "leads", label: "Leads" },
              ].map((t) => (
                <button
                  key={t.id}
                  type="button"
                  data-testid={`admin-tab-${t.id}`}
                  onClick={() => {
                    setTab(t.id);
                    setEditor(null);
                    setError("");
                    setNotice("");
                  }}
                  className={`rounded-full px-5 py-2.5 text-sm font-medium transition-colors duration-300 ${
                    tab === t.id ? "bg-[#10202E] text-[#F8F7F3]" : "text-[#4E6A82] hover:text-[#10202E]"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {notice && (
              <p data-testid="admin-notice" className="mt-6 text-sm text-emerald-700">
                {notice}
              </p>
            )}
            {error && authed && (
              <p data-testid="admin-error" className="mt-6 text-sm text-red-500">
                {error}
              </p>
            )}

            {tab === "enquiries" && (
              <div data-testid="admin-enquiry-list" className="mt-8">
                <div className="flex items-baseline justify-between">
                  <h1 className="font-display text-3xl text-[#10202E]">Enquiries</h1>
                  <span data-testid="admin-enquiry-count" className="font-mono text-xs tracking-[0.2em] text-[#6E8698]">
                    {enquiries.length} TOTAL
                  </span>
                </div>
                {enquiries.length === 0 ? (
                  <p className="mt-10 text-sm text-[#6E8698]">No enquiries yet.</p>
                ) : (
                  <div className="mt-8 space-y-4">
                    {enquiries.map((enq) => (
                      <div
                        key={enq.id}
                        data-testid={`enquiry-item-${enq.id.slice(0, 8)}`}
                        className="rounded-2xl border border-[#10202E]/[0.08] bg-white p-6 shadow-[0_2px_24px_rgba(16,32,46,0.05)]"
                      >
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div>
                            <p className="text-base font-medium text-[#10202E]">{enq.name}</p>
                            <p className="mt-0.5 font-mono text-[11px] tracking-wide text-[#6E8698]">
                              {enq.phone} · {enq.email}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="rounded-full border border-[#10202E]/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-[#6E8698]">
                              {enq.topic}
                            </span>
                            {["new", "contacted", "closed"].map((s) => (
                              <button
                                key={s}
                                type="button"
                                data-testid={`enquiry-status-${s}-${enq.id.slice(0, 8)}`}
                                onClick={() => updateStatus(enq.id, s)}
                                className={`rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors ${
                                  enq.status === s
                                    ? STATUS_STYLES[s]
                                    : "border-[#10202E]/10 text-[#8AA0B0] hover:text-[#33506A]"
                                }`}
                              >
                                {s}
                              </button>
                            ))}
                          </div>
                        </div>
                        <p className="mt-4 border-l-2 border-emerald-600/40 pl-4 text-sm font-light leading-relaxed text-[#4E6A82]">
                          {enq.message}
                        </p>
                        <p className="mt-3 font-mono text-[10px] tracking-[0.15em] text-[#8AA0B0]">
                          {new Date(enq.created_at).toLocaleString("en-IN")}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {tab === "journal" && (
              <div data-testid="admin-journal" className="mt-8">
                <div className="flex items-center justify-between">
                  <div className="flex items-baseline gap-4">
                    <h1 className="font-display text-3xl text-[#10202E]">Journal</h1>
                    <span data-testid="admin-article-count" className="font-mono text-xs tracking-[0.2em] text-[#6E8698]">
                      {articles.length} PUBLISHED
                    </span>
                  </div>
                  {!editor && (
                    <button
                      type="button"
                      data-testid="article-new-button"
                      onClick={openNew}
                      className="inline-flex items-center gap-2 rounded-full bg-emerald-400 px-5 py-2.5 text-xs font-semibold text-[#04120c] transition-colors hover:bg-emerald-300"
                    >
                      <Plus className="h-3.5 w-3.5" /> New article
                    </button>
                  )}
                </div>

                {editor ? (
                  <form data-testid="article-editor" onSubmit={saveArticle} className="mt-8 space-y-5 rounded-2xl border border-[#10202E]/[0.08] bg-white p-6 shadow-[0_2px_24px_rgba(16,32,46,0.05)] sm:p-8">
                    <input
                      data-testid="article-title-input"
                      value={draft.title}
                      onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                      placeholder="Article title"
                      className={inputCls}
                    />
                    <div className="grid gap-5 sm:grid-cols-2">
                      <select
                        data-testid="article-category-select"
                        value={draft.category}
                        onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                        className={inputCls}
                      >
                        {ARTICLE_CATEGORIES.map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </select>
                      <input
                        data-testid="article-time-input"
                        value={draft.time}
                        onChange={(e) => setDraft({ ...draft, time: e.target.value })}
                        placeholder="4 min read"
                        className={inputCls}
                      />
                    </div>
                    <textarea
                      data-testid="article-excerpt-input"
                      rows={2}
                      value={draft.excerpt}
                      onChange={(e) => setDraft({ ...draft, excerpt: e.target.value })}
                      placeholder="One-line excerpt shown on the article card"
                      className={`${inputCls} resize-none`}
                    />
                    <div>
                      <textarea
                        data-testid="article-body-input"
                        rows={10}
                        value={draft.body}
                        onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                        placeholder="Write the article here. Separate paragraphs with a blank line."
                        className={`${inputCls} resize-y`}
                      />
                      <p className="mt-2 font-mono text-[10px] tracking-[0.15em] text-[#8AA0B0]">
                        SEPARATE PARAGRAPHS WITH A BLANK LINE
                      </p>
                    </div>
                    <div>
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#8AA0B0]">
                          Cover art
                        </label>
                        <button
                          type="button"
                          data-testid="article-generate-cover-button"
                          onClick={generateCover}
                          disabled={generating || draft.title.trim().length < 5}
                          className="inline-flex items-center gap-2 rounded-full border border-emerald-600/40 bg-[#DDF7EC] px-4 py-2 text-xs font-semibold text-emerald-700 transition-colors hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                          {generating ? "Generating (up to a minute)…" : draft.cover ? "Regenerate cover" : "Generate cover"}
                        </button>
                      </div>
                      {draft.cover ? (
                        <img
                          data-testid="article-cover-preview"
                          src={draft.cover}
                          alt="Generated cover art"
                          className="mt-3 h-36 w-full rounded-xl border border-[#10202E]/10 object-cover"
                        />
                      ) : (
                        <p
                          data-testid="article-cover-empty"
                          className="mt-3 rounded-xl border border-dashed border-[#10202E]/15 px-4 py-5 text-xs font-light text-[#8AA0B0]"
                        >
                          No cover yet. Add a title, then generate matching 3D clay art — it publishes together with the article.
                        </p>
                      )}
                    </div>
                    <div className="flex gap-3">
                      <button
                        type="submit"
                        data-testid="article-save-button"
                        disabled={saving}
                        className="inline-flex items-center gap-2 rounded-full bg-emerald-400 px-6 py-3 text-sm font-semibold text-[#04120c] transition-colors hover:bg-emerald-300 disabled:opacity-60"
                      >
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editor.mode === "new" ? "Publish article" : "Save changes"}
                      </button>
                      <button
                        type="button"
                        data-testid="article-cancel-button"
                        onClick={() => setEditor(null)}
                        className="rounded-full border border-[#10202E]/20 px-6 py-3 text-sm font-medium text-[#33506A] transition-colors hover:border-red-400/60 hover:text-red-500"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="mt-8 space-y-4">
                    {articles.length === 0 ? (
                      <p className="text-sm text-[#6E8698]">No articles yet. Publish your first one.</p>
                    ) : (
                      articles.map((a) => (
                        <div
                          key={a.id}
                          data-testid={`article-item-${a.id.slice(0, 8)}`}
                          className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-[#10202E]/[0.08] bg-white p-5 shadow-[0_2px_24px_rgba(16,32,46,0.05)]"
                        >
                          <div className="min-w-0">
                            <div className="flex items-center gap-3">
                              <span
                                className="rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-[#10202E]"
                                style={{ background: CATEGORY_TINTS[a.category] || "#E8EFF4" }}
                              >
                                {a.category}
                              </span>
                              <span className="font-mono text-[10px] tracking-[0.15em] text-[#8AA0B0]">{a.time}</span>
                            </div>
                            <p className="mt-2 truncate text-base font-medium text-[#10202E]">{a.title}</p>
                          </div>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              data-testid={`article-edit-${a.id.slice(0, 8)}`}
                              onClick={() => openEdit(a)}
                              className="rounded-full border border-[#10202E]/15 p-2.5 text-[#33506A] transition-colors hover:border-emerald-600/50 hover:text-emerald-700"
                              aria-label="Edit article"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                            <button
                              type="button"
                              data-testid={`article-delete-${a.id.slice(0, 8)}`}
                              onClick={() => deleteArticle(a.id)}
                              className="rounded-full border border-[#10202E]/15 p-2.5 text-[#33506A] transition-colors hover:border-red-400/60 hover:text-red-500"
                              aria-label="Delete article"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}

            {tab === "leads" && (
              <div data-testid="admin-leads-list" className="mt-8">
                <div className="flex items-baseline justify-between">
                  <h1 className="font-display text-3xl text-[#10202E]">Leads</h1>
                  <span data-testid="admin-lead-count" className="font-mono text-xs tracking-[0.2em] text-[#6E8698]">
                    {leads.length} TOTAL
                  </span>
                </div>
                {leads.length === 0 ? (
                  <p className="mt-10 text-sm text-[#6E8698]">
                    No calculator leads yet. They're captured when a visitor emails themselves a
                    planning-tool result.
                  </p>
                ) : (
                  <div className="mt-8 space-y-4">
                    {leads.map((lead) => (
                      <div
                        key={lead.id}
                        data-testid={`lead-item-${lead.id.slice(0, 8)}`}
                        className="rounded-2xl border border-[#10202E]/[0.08] bg-white p-6 shadow-[0_2px_24px_rgba(16,32,46,0.05)]"
                      >
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <p className="text-base font-medium text-[#10202E]">{lead.email}</p>
                          <div className="flex items-center gap-2">
                            <span className="rounded-full border border-emerald-600/30 bg-[#DDF7EC] px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-emerald-700">
                              {lead.tool}
                            </span>
                            {lead.followed_up_at ? (
                              <span
                                data-testid={`lead-followed-up-${lead.id.slice(0, 8)}`}
                                className="rounded-full border border-[#10202E]/15 bg-[#10202E]/[0.04] px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-[#6E8698]"
                              >
                                Followed up {new Date(lead.followed_up_at).toLocaleDateString("en-IN")}
                              </span>
                            ) : (
                              <button
                                type="button"
                                data-testid={`lead-follow-up-${lead.id.slice(0, 8)}`}
                                onClick={() => sendFollowUp(lead.id)}
                                disabled={followUpId === lead.id}
                                className="inline-flex items-center gap-1.5 rounded-full border border-emerald-600/40 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-emerald-700 transition-colors hover:bg-[#DDF7EC] disabled:opacity-50"
                              >
                                {followUpId === lead.id ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <Send className="h-3 w-3" />
                                )}
                                Follow up
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="mt-3 space-y-1">
                          {lead.lines.map((line, i) => (
                            <p key={i} className="text-xs font-light text-[#4E6A82]">
                              {line}
                            </p>
                          ))}
                        </div>
                        <p className="mt-3 font-mono text-[10px] tracking-[0.15em] text-[#8AA0B0]">
                          {new Date(lead.created_at).toLocaleString("en-IN")}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
